/**
 * Adventure Unlock Checker
 * アドベンチャーモードの解放判定
 *
 * Created: 2025-12-17
 */

import type {
  AdventureProgress,
  NpcId,
  NpcUnlockStatus,
  ChapterConfig,
  AdventureNpcConfig,
} from '../data/types';
import {
  CHAPTERS,
  ADVENTURE_NPC_CONFIG,
  getNpcConfig,
  getChapterConfig,
} from '../data/constants';

// ============================================================
// NPC解放判定
// ============================================================

/**
 * NPCが解放されているかどうか
 */
export function isNpcUnlocked(
  progress: AdventureProgress,
  npcId: NpcId
): boolean {
  const config = getNpcConfig(npcId);
  if (!config) return false;

  // 解放条件がない（最初から解放）
  if (config.unlockRequirements.length === 0) {
    return true;
  }

  // 全ての解放条件NPCをクリアしているか
  return config.unlockRequirements.every((reqNpcId) =>
    progress.clearedNpcs.includes(reqNpcId)
  );
}

/**
 * NPCの解放状態を取得
 */
export function getNpcUnlockStatus(
  progress: AdventureProgress,
  npcId: NpcId
): NpcUnlockStatus {
  // クリア済み
  if (progress.clearedNpcs.includes(npcId)) {
    return 'cleared';
  }

  // 解放済み（挑戦可能）
  if (isNpcUnlocked(progress, npcId)) {
    return 'unlocked';
  }

  // 未解放
  return 'locked';
}

/**
 * 解放済みNPC一覧を取得
 */
export function getUnlockedNpcs(progress: AdventureProgress): NpcId[] {
  return ADVENTURE_NPC_CONFIG.filter((config) =>
    isNpcUnlocked(progress, config.npcId)
  ).map((config) => config.npcId);
}

/**
 * 挑戦可能なNPC一覧を取得（解放済み＆未クリア）
 */
export function getAvailableNpcs(progress: AdventureProgress): NpcId[] {
  return ADVENTURE_NPC_CONFIG.filter((config) => {
    const status = getNpcUnlockStatus(progress, config.npcId);
    return status === 'unlocked';
  }).map((config) => config.npcId);
}

/**
 * 次に解放されるNPCを取得
 */
export function getNextUnlockableNpcs(progress: AdventureProgress): NpcId[] {
  return ADVENTURE_NPC_CONFIG.filter((config) => {
    // 既に解放済みはスキップ
    if (isNpcUnlocked(progress, config.npcId)) {
      return false;
    }

    // 解放条件のうち、1つでもクリア済みがあるもの
    // （あと少しで解放されるNPC）
    const clearedCount = config.unlockRequirements.filter((reqNpcId) =>
      progress.clearedNpcs.includes(reqNpcId)
    ).length;

    return clearedCount > 0 && clearedCount < config.unlockRequirements.length;
  }).map((config) => config.npcId);
}

// ============================================================
// 章解放判定
// ============================================================

/**
 * 章が解放されているかどうか
 */
export function isChapterUnlocked(
  progress: AdventureProgress,
  chapterId: number
): boolean {
  const chapter = getChapterConfig(chapterId);
  if (!chapter) return false;

  // 解放条件がない（第1章）
  if (!chapter.unlockRequirement) {
    return true;
  }

  // 解放条件NPCをクリアしているか
  return progress.clearedNpcs.includes(chapter.unlockRequirement);
}

/**
 * 解放済みの章一覧を取得
 */
export function getUnlockedChapters(progress: AdventureProgress): number[] {
  return CHAPTERS.filter((chapter) =>
    isChapterUnlocked(progress, chapter.id)
  ).map((chapter) => chapter.id);
}

/**
 * 次に解放される章を取得
 */
export function getNextChapter(progress: AdventureProgress): number | null {
  const unlockedChapters = getUnlockedChapters(progress);
  const maxUnlocked = Math.max(...unlockedChapters, 0);

  // 次の章が存在するか
  const nextChapterId = maxUnlocked + 1;
  const nextChapter = getChapterConfig(nextChapterId);

  if (!nextChapter) return null;

  return nextChapterId;
}

// ============================================================
// 章の進捗
// ============================================================

/**
 * 章のクリア進捗を取得
 */
export function getChapterProgress(
  progress: AdventureProgress,
  chapterId: number
): {
  cleared: number;
  total: number;
  isComplete: boolean;
} {
  const chapter = getChapterConfig(chapterId);
  if (!chapter) {
    return { cleared: 0, total: 0, isComplete: false };
  }

  const total = chapter.npcs.length;
  const cleared = chapter.npcs.filter((npcId) =>
    progress.clearedNpcs.includes(npcId)
  ).length;

  return {
    cleared,
    total,
    isComplete: cleared >= total,
  };
}

/**
 * 章内のNPC設定を解放状態付きで取得
 */
export function getChapterNpcsWithStatus(
  progress: AdventureProgress,
  chapterId: number
): Array<{
  config: AdventureNpcConfig;
  status: NpcUnlockStatus;
}> {
  const chapter = getChapterConfig(chapterId);
  if (!chapter) return [];

  return chapter.npcs.map((npcId) => {
    const config = getNpcConfig(npcId);
    const status = getNpcUnlockStatus(progress, npcId);

    return {
      config: config!,
      status,
    };
  });
}

// ============================================================
// 解放条件の表示用
// ============================================================

/**
 * NPCの解放条件を文字列で取得
 */
export function getUnlockRequirementText(npcId: NpcId): string {
  const config = getNpcConfig(npcId);
  if (!config) return '';

  if (config.unlockRequirements.length === 0) {
    return '最初から解放';
  }

  // 必要なNPCの名前を取得
  const requiredNames = config.unlockRequirements.map((reqNpcId) => {
    const reqConfig = getNpcConfig(reqNpcId);
    return reqConfig ? getNpcDisplayName(reqNpcId) : reqNpcId;
  });

  if (requiredNames.length === 1) {
    return `${requiredNames[0]}を撃破`;
  }

  return `${requiredNames.join('と')}を撃破`;
}

/**
 * NPC IDから表示名を取得（competitors.tsとの連携用）
 * 注: 実際の実装ではcompetitors.tsからインポート
 */
function getNpcDisplayName(npcId: NpcId): string {
  // 仮の実装（後でcompetitors.tsと連携）
  const nameMap: Record<NpcId, string> = {
    elda: '森の賢者エルダ',
    sisterEmma: 'シスター・エマ',
    seira: '氷姫セイラ',
    misty: 'ミスティ',
    pippo: 'ピッポ',
    rowen: '聖騎士ロウェン',
    garon: '獣王ガロン',
    toma: '新人トーマ',
    malik: '闇術師マリク',
    liliane: 'リリアーヌ',
    nova: '機工士ノヴァ',
    profNome: 'プロフ・ノーム',
    galdos: 'ガルドス',
    kain: '炎帝カイン',
    mask: '謎の仮面',
    doppelganger: 'ドッペルゲンガー',
  };

  return nameMap[npcId] || npcId;
}

// ============================================================
// 解放時の新規アンロック取得
// ============================================================

/**
 * NPC撃破時に新たに解放されるNPCを取得
 */
export function getNewUnlocksOnClear(
  progress: AdventureProgress,
  clearedNpcId: NpcId
): NpcId[] {
  // 撃破後の状態をシミュレート
  const updatedClearedNpcs = [...progress.clearedNpcs, clearedNpcId];

  return ADVENTURE_NPC_CONFIG.filter((config) => {
    // 既に解放済みはスキップ
    if (isNpcUnlocked(progress, config.npcId)) {
      return false;
    }

    // 撃破後に解放条件を満たすか
    return config.unlockRequirements.every((reqNpcId) =>
      updatedClearedNpcs.includes(reqNpcId)
    );
  }).map((config) => config.npcId);
}

/**
 * NPC撃破時に新たに解放される章を取得
 */
export function getNewChapterOnClear(
  progress: AdventureProgress,
  clearedNpcId: NpcId
): number | null {
  // 撃破後の状態をシミュレート
  const updatedClearedNpcs = [...progress.clearedNpcs, clearedNpcId];

  for (const chapter of CHAPTERS) {
    // 既に解放済みはスキップ
    if (isChapterUnlocked(progress, chapter.id)) {
      continue;
    }

    // 撃破後に解放条件を満たすか
    if (
      chapter.unlockRequirement &&
      updatedClearedNpcs.includes(chapter.unlockRequirement)
    ) {
      return chapter.id;
    }
  }

  return null;
}
